import React, { useEffect } from "react";
import { AuthProvider, useAuth, type AuthProviderProps } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ConfirmModalProvider from "@ui/Modal/ConfirmProvider";
import ToastProvider from "@ui/Toast/Provider";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { RouterProvider } from "react-router-dom";
import { ChannelMuliplexerProvider } from "./api/ws/useChannelMultiplexer";
import { router } from "./router";
import manageFocusMode from "./lib/manageFocusMode";
import { WebStorageStateStore, type User } from "oidc-client-ts";
import { MTS_ENV } from "./lib/setupEnv";
import Loading from "@ui/Loading";
import usePostUserKeycloakRoles from "./api/usePostUserKeycloakRoles";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      structuralSharing: false, // we don't need it, very expensive
      refetchOnReconnect: false, // not needed, WS
      networkMode: "always", // no pausing on offline https://tanstack.com/query/v4/docs/framework/react/guides/network-mode/#network-mode-online
      retry: false, // no FUD
      cacheTime: 10 * 60_000, // gcTime, 10 minutes
    },
  },
});

const emotionCache = createCache({
  key: "emotion-with-nonce",
  nonce: window.__webpack_nonce__,
});

const OAUTH_PATH = "oauth/signin";

const { protocol, host } = window.location;

const authConfiguration: AuthProviderProps = {
  authority: MTS_ENV.OIDC_AUTHORITY,
  client_id: MTS_ENV.OIDC_CLIENT_ID,
  redirect_uri: `${protocol}//${host}${OAUTH_PATH}`,
  //   post_logout_redirect_uri: `${protocol}//${host}`,
  response_type: "code",
  scope: "openid profile email",
  // silent renew will get a new access_token via an iframe
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 120,
  // will revoke (reference) access tokens at logout time
  revokeTokensOnSignout: true,
  userStore: new WebStorageStateStore({ store: window.localStorage, prefix: "oidc." }),
  onSigninCallback: () => window.history.replaceState({}, document.title, window.location.pathname),
};

function getAuthCookieDomainValue() {
  const domain = window.location.hostname.match(/([a-z0-9-]+\.(?:[a-z]{2,}|co\.[a-z]{2,}))$/i);

  return domain?.[0] ? ` Domain=.${domain[0]}; ` : "";
}

function syncAuthCookie(user: User) {
  document.cookie = `auth_token=${user.access_token};${getAuthCookieDomainValue()}path=/; max-age=${user.expires_in}; samesite; secure`;
}

function clearAuthCookie() {
  document.cookie = `auth_token=;${getAuthCookieDomainValue()}path=/; max-age=0; samesite; secure`;
}

manageFocusMode();

export const App = () => (
  <AuthProvider {...authConfiguration}>
    <QueryClientProvider client={queryClient}>
      <ChannelMuliplexerProvider>
        <AuthBoundary>
          <ConfirmModalProvider>
            <ToastProvider>
              <CacheProvider value={emotionCache}>
                <RouterProvider router={router} />
              </CacheProvider>
            </ToastProvider>
          </ConfirmModalProvider>
        </AuthBoundary>
      </ChannelMuliplexerProvider>
    </QueryClientProvider>
  </AuthProvider>
);

const AuthBoundary = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();

  const postUserKeycloakRoles = usePostUserKeycloakRoles();

  useEffect(() => {
    const onUserLoaded = (user: User) => syncAuthCookie(user);
    const onSilentRenewError = () => {
      clearAuthCookie();
      void auth.signinRedirect({ redirect_uri: window.location.href });
    };

    auth.events.addUserLoaded(onUserLoaded);
    auth.events.addSilentRenewError(onSilentRenewError);

    return () => {
      auth.events.removeUserLoaded(onUserLoaded);
      auth.events.removeSilentRenewError(onSilentRenewError);
    };
  }, [auth]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      syncAuthCookie(auth.user);
      void postUserKeycloakRoles({});
      return;
    }

    if (!auth.isLoading) {
      clearAuthCookie();
    }
  }, [
    auth.user,
    auth.isAuthenticated,
    auth.isLoading,
    auth.user?.access_token,
    auth.user?.expires_in,
    postUserKeycloakRoles,
  ]);

  if (auth.isLoading) return <Loading />;

  if (!auth.isAuthenticated && !auth.isLoading) {
    void auth.signinRedirect({
      redirect_uri: window.location.href,
    });
    return null;
  }

  return children;
};
